/**
 * Local screen-share preview.
 * Remotes get the full published track. Locally we use a low-FPS clone.
 * On stage fullscreen, the last frame is frozen synchronously so the live
 * capture cannot recurse — and the stage is never blank.
 */

import { useEffect, useRef, type CSSProperties } from "react";
import { isTrackReference, type TrackReference } from "@livekit/components-react";

type Props = {
  trackRef: TrackReference;
  style?: CSSProperties;
  /** Freeze last frame (stage fullscreen). Also armed by STAGE_FULLSCREEN_EVENT. */
  freeze?: boolean;
};

const LOCAL_PREVIEW_MAX_FPS = 6;

export const STAGE_FULLSCREEN_EVENT = "codelive:before-stage-fullscreen";

export function notifyBeforeStageFullscreen(): void {
  try {
    window.dispatchEvent(new CustomEvent(STAGE_FULLSCREEN_EVENT));
  } catch {
    /* ignore */
  }
}

function paintFreeze(
  video: HTMLVideoElement | null,
  canvas: HTMLCanvasElement | null,
): boolean {
  if (!video || !canvas) return false;
  const w = video.videoWidth;
  const h = video.videoHeight;
  if (!w || !h) return false;
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) return false;
  ctx.drawImage(video, 0, 0, w, h);
  return true;
}

export function LocalScreenSharePreview({ trackRef, style, freeze = false }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const el = videoRef.current;
    if (!el || !isTrackReference(trackRef)) return;

    const liveTrack = trackRef.publication?.track;
    if (!liveTrack) return;

    const mediaTrack = liveTrack.mediaStreamTrack;
    let clone: MediaStreamTrack | null = null;
    let usedClone = false;

    const cleanupAttach = () => {
      try {
        liveTrack.detach(el);
      } catch {
        /* ignore */
      }
      el.srcObject = null;
    };

    if (mediaTrack && typeof mediaTrack.clone === "function") {
      try {
        clone = mediaTrack.clone();
        void clone
          .applyConstraints({
            frameRate: { ideal: LOCAL_PREVIEW_MAX_FPS, max: LOCAL_PREVIEW_MAX_FPS },
          })
          .catch(() => undefined);
        el.srcObject = new MediaStream([clone]);
        usedClone = true;
        void el.play().catch(() => undefined);
      } catch {
        clone?.stop();
        clone = null;
      }
    }

    if (!usedClone) {
      liveTrack.attach(el);
      void el.play().catch(() => undefined);
    }

    return () => {
      cleanupAttach();
      try {
        clone?.stop();
      } catch {
        /* ignore */
      }
    };
  }, [
    trackRef.participant.identity,
    trackRef.publication?.trackSid,
    trackRef.publication?.track,
  ]);

  // Sync freeze on click (before FS) so React batching cannot flash blank.
  useEffect(() => {
    const showFrozen = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      paintFreeze(video, canvas);
      if (canvas) canvas.style.display = "block";
      if (video) video.style.visibility = "hidden";
    };
    const showLive = () => {
      const video = videoRef.current;
      const canvas = canvasRef.current;
      if (canvas) canvas.style.display = "none";
      if (video) video.style.visibility = "visible";
    };

    const onBeforeFs = () => showFrozen();
    const onFsChange = () => {
      if (!document.fullscreenElement) showLive();
    };

    window.addEventListener(STAGE_FULLSCREEN_EVENT, onBeforeFs);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => {
      window.removeEventListener(STAGE_FULLSCREEN_EVENT, onBeforeFs);
      document.removeEventListener("fullscreenchange", onFsChange);
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (freeze) {
      paintFreeze(video, canvas);
      if (canvas) canvas.style.display = "block";
      if (video) video.style.visibility = "hidden";
    } else if (!document.fullscreenElement) {
      if (canvas) canvas.style.display = "none";
      if (video) video.style.visibility = "visible";
    }
  }, [freeze]);

  return (
    <>
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        disablePictureInPicture
        style={style}
      />
      <canvas
        ref={canvasRef}
        style={{
          ...style,
          display: "none",
          pointerEvents: "none",
        }}
      />
    </>
  );
}
